"use client";

import { useState } from "react";
import { CheckCircle2, Users, Building, Laptop, Briefcase } from "lucide-react";
import { useTranslations } from "next-intl";
import { cn } from "~/lib/utils";
import { motion, AnimatePresence } from "framer-motion";

export function TargetAudience() {
  const t = useTranslations("landing.targetAudience");
  const [activeTab, setActiveTab] = useState<"owners" | "syndics">("owners");

  const ownerBenefits = [
    t("owners.benefits.0"),
    t("owners.benefits.1"),
    t("owners.benefits.2"),
    t("owners.benefits.3"),
    t("owners.benefits.4"),
  ];

  const syndicBenefits = [
    t("syndics.benefits.0"),
    t("syndics.benefits.1"),
    t("syndics.benefits.2"),
    t("syndics.benefits.3"),
    t("syndics.benefits.4"),
  ];

  return (
    <section className="border-y border-gray-200 bg-gray-50 py-24 dark:border-gray-800 dark:bg-gray-900/50">
      <div className="container mx-auto px-4">
        <div className="mx-auto mb-12 max-w-3xl text-center">
          <h2 className="mb-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl dark:text-white">
            {t("title")}
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300">
            {t("subtitle")}
          </p>
        </div>

        {/* Tabs Container */}
        <div className="mx-auto max-w-5xl">
          {/* Pill Tabs */}
          <div className="mb-8 flex justify-center">
            <div className="inline-flex rounded-full border border-gray-200 bg-white/50 p-1 shadow-sm backdrop-blur-sm dark:border-gray-700 dark:bg-gray-800/50">
              <button
                onClick={() => setActiveTab("owners")}
                className={cn(
                  "rounded-full px-6 py-2.5 text-sm font-medium transition-all duration-200",
                  activeTab === "owners"
                    ? "bg-white text-teal-600 shadow-sm ring-1 ring-gray-200 dark:bg-gray-900 dark:text-teal-400 dark:ring-gray-700"
                    : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200",
                )}
              >
                {t("owners.tab")}
              </button>
              <button
                onClick={() => setActiveTab("syndics")}
                className={cn(
                  "rounded-full px-6 py-2.5 text-sm font-medium transition-all duration-200",
                  activeTab === "syndics"
                    ? "bg-white text-blue-600 shadow-sm ring-1 ring-gray-200 dark:bg-gray-900 dark:text-blue-400 dark:ring-gray-700"
                    : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200",
                )}
              >
                {t("syndics.tab")}
              </button>
            </div>
          </div>

          {/* The Card */}
          <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-xl dark:border-gray-800 dark:bg-gray-900">
            <div className="relative min-h-[400px]">
              <AnimatePresence mode="wait">
                {activeTab === "owners" ? (
                  <motion.div
                    key="owners"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3 }}
                    className="grid md:grid-cols-2"
                  >
                    {/* Left: Content */}
                    <div className="flex flex-col justify-center p-8 lg:p-12">
                      <div className="mb-6 flex items-center gap-3">
                        <div className="rounded-xl bg-teal-50 p-2.5 dark:bg-teal-900/30">
                          <Users className="h-6 w-6 text-teal-600 dark:text-teal-400" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                          {t("owners.tab")}
                        </h3>
                      </div>
                      <ul className="space-y-4">
                        {ownerBenefits.map((benefit, index) => (
                          <li key={index} className="flex items-start gap-3">
                            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-teal-500" />
                            <span className="leading-relaxed text-gray-600 dark:text-gray-300">
                              {benefit}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    {/* Right: Visual */}
                    <div className="flex min-h-[300px] items-center justify-center border-t border-gray-100 bg-teal-50/50 p-8 md:min-h-full md:border-t-0 md:border-l dark:border-gray-800 dark:bg-teal-900/20">
                      <div className="relative flex aspect-square w-full max-w-sm items-center justify-center">
                        <div className="absolute inset-0 rounded-full bg-teal-500/10 blur-3xl dark:bg-teal-500/20" />
                        <div className="relative rounded-3xl border border-gray-100 bg-white p-8 shadow-xl dark:border-gray-700 dark:bg-gray-800">
                          <Building
                            className="h-24 w-24 text-teal-500 dark:text-teal-400"
                            strokeWidth={1}
                          />
                          <div className="absolute -right-6 -bottom-6 rounded-2xl border border-gray-100 bg-white p-4 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                            <Users className="h-10 w-10 text-teal-600 dark:text-teal-300" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="syndics"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                    className="grid md:grid-cols-2"
                  >
                    {/* Left: Content */}
                    <div className="flex flex-col justify-center p-8 lg:p-12">
                      <div className="mb-6 flex items-center gap-3">
                        <div className="rounded-xl bg-blue-50 p-2.5 dark:bg-blue-900/30">
                          <Briefcase className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                          {t("syndics.tab")}
                        </h3>
                      </div>
                      <ul className="space-y-4">
                        {syndicBenefits.map((benefit, index) => (
                          <li key={index} className="flex items-start gap-3">
                            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-blue-500" />
                            <span className="leading-relaxed text-gray-600 dark:text-gray-300">
                              {benefit}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    {/* Right: Visual */}
                    <div className="flex min-h-[300px] items-center justify-center border-t border-gray-100 bg-blue-50/50 p-8 md:min-h-full md:border-t-0 md:border-l dark:border-gray-800 dark:bg-blue-900/20">
                      <div className="relative flex aspect-square w-full max-w-sm items-center justify-center">
                        <div className="absolute inset-0 rounded-full bg-blue-500/10 blur-3xl dark:bg-blue-500/20" />
                        <div className="relative rounded-3xl border border-gray-100 bg-white p-8 shadow-xl dark:border-gray-700 dark:bg-gray-800">
                          <Laptop
                            className="h-24 w-24 text-blue-500 dark:text-blue-400"
                            strokeWidth={1}
                          />
                          <div className="absolute -bottom-6 -left-6 rounded-2xl border border-gray-100 bg-white p-4 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                            <Briefcase className="h-10 w-10 text-blue-600 dark:text-blue-300" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
